import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { ChatExclamationmarkIcon, ExclamationmarkTriangleIcon } from '@navikt/aksel-icons';
import { CurrentQuestionAnalyzed } from '../../pages/AnalyzeSurvey';
import { agreementOf } from '../../domain/psychological-safety/team-result';
import {
  agreementTones,
  changeTone,
  formatChange,
  formatScore,
  getScoreLevel,
  levelTones,
  MAX_SCORE,
} from '../AnalysisLayout/analysis.utils';
import classes from './AnalyzeQuestion.module.css';

type AnalyzeQuestionProps = {
  question: CurrentQuestionAnalyzed;
  /** Change in the average since the previous survey, when there is one */
  change?: number;
};

const RING_RADIUS = 56;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export const AnalyzeQuestion = ({ question, change }: AnalyzeQuestionProps) => {
  const { t } = useTranslation();
  const headingId = `question-${question.questionId}-heading`;

  const level = getScoreLevel(question.score);
  const tone = levelTones[level];
  const score = formatScore(question.score);
  const filled = RING_CIRCUMFERENCE * Math.min(1, Math.max(0, question.score / MAX_SCORE));

  const agreement = agreementOf(question.standardDeviation);
  const agreementTone = agreement ? agreementTones[agreement] : null;

  return (
    <div className={classes.container}>
      <article aria-labelledby={headingId} className={classes.card}>
        <div className={classes.intro}>
          <div className={classes.introText}>
            <Heading id={headingId} level={1} data-size="lg" className={classes.question}>
              {question.question}
            </Heading>
            <Paragraph data-size="lg" className={classes.lead}>
              {t(`analysis.scoreSummary.${level}`, { score })}
            </Paragraph>
          </div>
          <div className={classes.scoreArea}>
            <figure className={classes.ring} data-tone={tone}>
              <svg aria-hidden="true" width="132" height="132" viewBox="0 0 132 132">
                <circle cx="66" cy="66" r={RING_RADIUS} className={classes.ringTrack} />
                <circle
                  cx="66"
                  cy="66"
                  r={RING_RADIUS}
                  className={classes.ringValue}
                  strokeDasharray={`${filled} ${RING_CIRCUMFERENCE}`}
                />
              </svg>
              <figcaption className={classes.ringText} aria-label={t('analysis.scoreAria', { score })}>
                <span aria-hidden="true" className={classes.scoreValue}>
                  {score}
                </span>
                <span aria-hidden="true" className={classes.subtle}>
                  {t('analysis.scoreOf')}
                </span>
              </figcaption>
            </figure>
            <dl className={classes.facts}>
              <div>
                <dt className={classes.subtle}>{t('analysis.level.label')}</dt>
                <dd>
                  <Tag data-size="sm" data-color={tone}>
                    {t(`analysis.level.${level}`)}
                  </Tag>
                </dd>
              </div>
              {agreement && (
                <div>
                  <dt className={classes.subtle}>{t('analysis.agreement')}</dt>
                  <dd>
                    <Tag data-size="sm" data-color={agreementTone!}>
                      {t(`teamResult.agreement.${agreement}`)}
                    </Tag>
                  </dd>
                </div>
              )}
              {change !== undefined && (
                <div>
                  <dt className={classes.subtle}>{t('analysis.trend')}</dt>
                  <dd className={classes.trend} data-tone={changeTone(change)}>
                    {formatChange(change)}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        <div className={classes.details}>
          <section aria-labelledby={`${headingId}-meaning`} className={classes.detail}>
            <div className={classes.detailHeader}>
              <span aria-hidden="true" className={classes.detailIcon}>
                <ChatExclamationmarkIcon />
              </span>
              <Heading id={`${headingId}-meaning`} level={2} data-size="xs">
                {t('analysis.meaning')}
              </Heading>
            </div>
            <Paragraph>{question.questionExplanation}</Paragraph>
            {agreement && (
              <Paragraph data-size="sm" className={classes.agreementNote} data-tone={agreementTone!}>
                {t(`teamResult.agreementExplanation.${agreement}`)}
              </Paragraph>
            )}
          </section>
          <section aria-labelledby={`${headingId}-consequences`} className={classes.detail}>
            <div className={classes.detailHeader}>
              <span aria-hidden="true" className={`${classes.detailIcon} ${classes.warningIcon}`}>
                <ExclamationmarkTriangleIcon />
              </span>
              <Heading id={`${headingId}-consequences`} level={2} data-size="xs">
                {t('analysis.consequences')}
              </Heading>
            </div>
            <ul className={classes.list}>
              {question.consequences.map((consequence) => (
                <li key={consequence}>{consequence}</li>
              ))}
            </ul>
          </section>
        </div>
      </article>

      {question.tips.map((tip, index) => (
        <section
          key={`${tip.what}-${index}`}
          aria-labelledby={`${headingId}-action-${index}`}
          className={classes.actions}
          data-color="brand1"
        >
          <div className={classes.actionHeader}>
            <span className={classes.eyebrow}>
              {question.tips.length > 1
                ? t('analysis.actions.eyebrowNumbered', { number: index + 1, total: question.tips.length })
                : t('analysis.actions.eyebrow')}
            </span>
            <Heading id={`${headingId}-action-${index}`} level={2} data-size="md" className={classes.actionTitle}>
              {tip.what}
            </Heading>
          </div>
          <div className={classes.actionCards}>
            <div className={classes.actionCard}>
              <Heading level={3} data-size="2xs">
                {t('analysis.actions.how')}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {tip.how}
              </Paragraph>
            </div>
            <div className={classes.actionCard}>
              <Heading level={3} data-size="2xs">
                {t('analysis.actions.example')}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {tip.examplesOfHow}
              </Paragraph>
            </div>
            <div className={classes.actionCard}>
              <Heading level={3} data-size="2xs">
                {t('analysis.actions.pitfall')}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {tip.danger}
              </Paragraph>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};
